import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Opts } from "../types";
import { useIsMounted } from "./useIsMounted";

const DEFAULT_LABEL_FIELD = "label";
export const ERROR_LABEL = "An error occurred loading this option";

type GetterService = {
  get: (id: string, opts: Opts) => Promise<{ [key: string]: any }>;
};

type GetterWithFieldsArg = {
  service: GetterService;
  label: string;
};

export type ArgType = GetterWithFieldsArg | GetterService;

export type KeyType = string | number;

export type ItemType = { [key in KeyType]: string };

export const useFieldLookups = (
  serviceArg: ArgType,
  ids: KeyType[]
): ItemType | null => {
  const argAsGetter = serviceArg as GetterService;
  const argAsGetterWithFields = serviceArg as GetterWithFieldsArg;
  const isGetterWithFieldsArg = !!argAsGetterWithFields.service;

  const { service, label: labelField } = isGetterWithFieldsArg
    ? {
        service: argAsGetterWithFields.service,
        label: argAsGetterWithFields.label,
      }
    : {
        service: argAsGetter,
        label: DEFAULT_LABEL_FIELD,
      };

  const getItemResult = (id: KeyType) =>
    service
      .get(String(id), { token })
      .then((item) => ({
        id,
        label: String(item[labelField]),
      }))
      .catch(() => ({ id, label: ERROR_LABEL }));

  const token: string =
    useSelector((state: any) => state?.auth?.user?.token) || undefined;

  const [items, setItems] = useState<ItemType | null>(null);
  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!token) {
      console.warn(`
        useFieldLookup has not found an auth token in redux at the location:
        state -> auth -> user -> token
        The subsequent api call will fail unless it is a non-authed endpoint`);
    }

    const itemResults = Promise.all(
      getUniqueIds(ids).map((id) => getItemResult(id))
    );

    itemResults.then(
      (items) => isMountedRef.current && setItems(resultBuilder(items))
    );
  }, [ids]);

  return items;
};

const resultBuilder = (
  itemresults: {
    id: KeyType;
    label: string;
  }[]
) =>
  itemresults.reduce((prev, curr) => {
    prev[curr.id] = curr.label;
    return prev;
  }, {} as ItemType);

const getUniqueIds = (items: KeyType[]) =>
  items.filter((value, index, self) => self.indexOf(value) === index);
